import React, { Component } from 'react';
import {
    MagnifyingGlassIcon,
    FunnelIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    CalendarIcon,
    Bars3Icon,
} from '@heroicons/react/20/solid';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import ClipLoader from 'react-spinners/ClipLoader';
import Breadcrumb from '../../Shared/BreadCumb';
import ResponsiveTable from '../../Shared/ResponsiveTable';
import NoAccesComponent from '../../Shared/NoAccesComponent';
import PenalitieDetailModal from '../../Shared/PenalitieDetailModal';
import { getAllPenalities } from '../penalities/services/penalities';
import { getOfficials } from '../officials/services/officials';

const BREADCRUMB = [
    { name: 'Multas', href: '#', current: false },
    { name: 'Multas por Funcionario', href: '#', current: true },
];

const STATUS_BADGE = (value) => {
    const map = {
        'PENDIENTE POR PAGAR': 'bg-red-100 text-red-600',
        'PAGO PARCIAL': 'bg-yellow-100 text-yellow-600',
        'PAGADA': 'bg-green-100 text-green-600',
        'MOROSIDAD': 'bg-orange-100 text-orange-600',
        'LLAMADO DE ATENCIÓN': 'bg-gray-100 text-gray-600',
        'REVOCADA': 'bg-gray-100 text-gray-600',
    };
    const cls = map[value] || 'bg-gray-100 text-gray-600';
    const label = value === 'MOROSIDAD' ? 'MOROSA' : value;
    return <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
};

class OfficialFines extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // permisos
            loadingPermissions: true,
            canSee: false,

            // funcionarios
            loadingOfficials: false,
            officials: [],
            selectedOfficialId: 0,

            // filtros
            officialIdentification: '',
            userName: '',
            dateStart: '',
            dateEnd: '',
            limit: 100,

            // datos
            loading: false,
            fines: [],

            // UI
            isFiltersOpen: true,
            openMenuId: null,
            menuAnchorEl: null,

            // detalle
            detailOpen: false,
            selectedFine: null,
        };
    }

    componentDidMount() {
        let permissions = window.localStorage.getItem('permissons');
        const userType = window.localStorage.getItem('userType');
        if (userType === 'ANALISTA') {
            try {
                permissions = JSON.parse(permissions) || [{}];
            } catch (e) {
                permissions = [{}];
            }
            const entries = permissions?.[0]?.officialFines || [];
            let canSee = false;
            entries.forEach((el) => {
                if (el.name === 'see' && el.value === true) canSee = true;
            });
            this.setState({ canSee, loadingPermissions: false }, () => {
                if (canSee) this.loadOfficials();
            });
        } else {
            this.setState({ canSee: true, loadingPermissions: false }, () => this.loadOfficials());
        }
    }

    loadOfficials = () => {
        this.setState({ loadingOfficials: true });
        getOfficials({ limit: 5000 })
            .then((res) => {
                const data = res?.data?.data || [];
                this.setState({ officials: data, loadingOfficials: false });
            })
            .catch(() => this.setState({ loadingOfficials: false }));
    };

    onSelectOfficial = (e) => {
        const id = parseInt(e.target.value, 10) || 0;
        const official = this.state.officials.find((o) => o.id === id);
        this.setState({
            selectedOfficialId: id,
            officialIdentification: official?.identification || '',
            userName: official?.userName || '',
        });
    };

    buildBody = () => {
        const body = {};
        const s = this.state;
        if (s.selectedOfficialId) body.idOfficial = s.selectedOfficialId;
        if (s.officialIdentification) body.officialIdentification = s.officialIdentification;
        if (s.userName) body.userName = s.userName;
        if (s.dateStart) body.dateStart = new Date(s.dateStart).toISOString();
        if (s.dateEnd) body.dateEnd = new Date(s.dateEnd).toISOString();
        body.limit = parseInt(s.limit, 10);
        body.status = -1;
        return body;
    };

    fetchData = () => {
        if (!this.state.selectedOfficialId && !this.state.officialIdentification && !this.state.userName) {
            toast('Debe seleccionar un funcionario o indicar cédula/usuario');
            return;
        }
        const body = this.buildBody();
        this.setState({ loading: true });
        getAllPenalities(body)
            .then((result) => {
                if (result && result.status === 200) {
                    const fines = [];
                    (result.data?.data || []).forEach((item) => {
                        if (item.headerFine) {
                            item.headerFine.forEach((fine) => {
                                fines.push({
                                    ...fine,
                                    identificationUser: item.identification,
                                    fullnameUser: `${item.firstName || ''} ${item.middleName || ''}`.trim(),
                                    email: item.email,
                                    userEmail: item.email,
                                    phone: item.phone ? item.phone[0] : null,
                                });
                            });
                        }
                    });
                    fines.sort((a, b) => Date.parse(b.dateReg) - Date.parse(a.dateReg));
                    this.setState({ fines, loading: false });
                } else {
                    toast(typeof result === 'string' ? result : 'No se encontraron resultados');
                    this.setState({ fines: [], loading: false });
                }
            })
            .catch((err) => {
                toast('Error al consultar multas');
                this.setState({ loading: false });
                console.log(err);
            });
    };

    clearFilters = () => {
        this.setState({
            selectedOfficialId: 0,
            officialIdentification: '',
            userName: '',
            dateStart: '',
            dateEnd: '',
            limit: 100,
        });
    };

    toggleMenu = (id, anchorEl) => {
        this.setState((prev) => ({
            openMenuId: prev.openMenuId === id ? null : id,
            menuAnchorEl: anchorEl,
        }));
    };

    openDetail = (row) => {
        this.setState({ selectedFine: row, detailOpen: true, openMenuId: null, menuAnchorEl: null });
    };

    get columns() {
        return [
            { field: 'fullnameUser', headerName: 'Nombre completo' },
            { field: 'identificationUser', headerName: 'Identificación' },
            { field: 'code', headerName: 'Código de la Multa' },
            { field: 'dateReg', headerName: 'Fecha de registro' },
            {
                field: 'descripStatus',
                headerName: 'Estatus',
                renderCell: (params) => STATUS_BADGE(params.row.descripStatus),
            },
            { field: 'numberPlate', headerName: 'Placa' },
            { field: 'totalBS', headerName: 'Monto' },
            { field: 'remainder', headerName: 'Restante' },
        ];
    }

    renderActions = (row) => {
        const isOpen = this.state.openMenuId === row.idHeaderFine;
        return (
            <>
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        this.toggleMenu(row.idHeaderFine, e.currentTarget);
                    }}
                    aria-haspopup="true"
                    title="Acciones"
                >
                    <Bars3Icon className="h-5 w-5 text-emerald-900" />
                </IconButton>
                <Menu
                    anchorEl={this.state.menuAnchorEl}
                    open={isOpen}
                    onClose={() => this.toggleMenu(row.idHeaderFine, null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    MenuListProps={{ onClick: (e) => e.stopPropagation() }}
                >
                    <MenuItem onClick={() => this.openDetail(row)}>
                        <ListItemIcon><MagnifyingGlassIcon className="h-5 w-5 text-gray-900" /></ListItemIcon>
                        <ListItemText primary="Ver detalle" />
                    </MenuItem>
                </Menu>
            </>
        );
    };

    renderFilters() {
        const s = this.state;
        return (
            <div className="mt-8 bg-white rounded-lg shadow-sm ring-1 ring-gray-200">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                        <FunnelIcon className="h-5 w-5 text-gray-500" />
                        <h3 className="text-lg font-semibold text-gray-900">Filtros de Búsqueda</h3>
                    </div>
                    <button
                        onClick={() => this.setState({ isFiltersOpen: !s.isFiltersOpen })}
                        className="flex items-center text-sm text-gray-600 hover:text-gray-900"
                    >
                        {s.isFiltersOpen ? 'Ocultar' : 'Mostrar'} filtros
                        {s.isFiltersOpen ? <ChevronUpIcon className="ml-1 h-4 w-4" /> : <ChevronDownIcon className="ml-1 h-4 w-4" />}
                    </button>
                </div>

                {s.isFiltersOpen && (
                    <div className="p-6">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <div className="lg:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Funcionario {s.loadingOfficials && <ClipLoader color="gray" size={12} />}
                                </label>
                                <select
                                    value={s.selectedOfficialId}
                                    onChange={this.onSelectOfficial}
                                    className="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                                >
                                    <option value={0}>— Seleccione un funcionario —</option>
                                    {s.officials.map((o) => (
                                        <option key={o.id} value={o.id}>
                                            {`${o.firstName || ''} ${o.firstLastName || ''}`.trim()}
                                            {o.code ? ` · Código ${o.code}` : ''}
                                            {o.identification ? ` · CI ${o.identification}` : ''}
                                            {o.userName ? ` · ${o.userName}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Cédula del funcionario</label>
                                <input value={s.officialIdentification} onChange={(e) => this.setState({ officialIdentification: e.target.value, selectedOfficialId: 0 })} onKeyDown={(e) => e.key === 'Enter' && this.fetchData()} type="text" className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Usuario del funcionario</label>
                                <input value={s.userName} onChange={(e) => this.setState({ userName: e.target.value, selectedOfficialId: 0 })} onKeyDown={(e) => e.key === 'Enter' && this.fetchData()} type="text" className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2"><CalendarIcon className="inline h-4 w-4 mr-1" />Fecha Desde</label>
                                <input value={s.dateStart} onChange={(e) => this.setState({ dateStart: e.target.value })} type="date" className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2"><CalendarIcon className="inline h-4 w-4 mr-1" />Fecha Hasta</label>
                                <input value={s.dateEnd} onChange={(e) => this.setState({ dateEnd: e.target.value })} type="date" className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Límite</label>
                                <select value={s.limit} onChange={(e) => this.setState({ limit: parseInt(e.target.value, 10) })} className="block w-full rounded-md border-0 py-2 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm">
                                    <option value={10}>10 registros</option>
                                    <option value={50}>50 registros</option>
                                    <option value={100}>100 registros</option>
                                    <option value={500}>500 registros</option>
                                    <option value={1000}>1000 registros</option>
                                </select>
                            </div>
                        </div>

                        <div className="mt-6 flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={this.fetchData}
                                type="button"
                                style={{ height: 40 }}
                                className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-blue-950 rounded-md shadow-sm hover:bg-blue-900"
                            >
                                <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                                Buscar Multas
                            </button>
                            <button
                                onClick={this.clearFilters}
                                type="button"
                                style={{ height: 40 }}
                                className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-red-700 rounded-md shadow-sm hover:bg-red-600"
                            >
                                Limpiar
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    renderBody() {
        return (
            <div className="max-w-7xl mx-auto px-4">
                <div className="py-6">
                    <Breadcrumb pages={BREADCRUMB} />

                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Multas por Funcionario</h2>
                        <p className="text-gray-600">Consulta las multas impuestas por un funcionario específico.</p>
                    </div>

                    {this.renderFilters()}

                    {this.state.loading && (
                        <div className="text-center py-12">
                            <ClipLoader color={'#1e40af'} loading={true} size={50} />
                            <p className="mt-2 text-sm text-gray-600">Cargando multas...</p>
                        </div>
                    )}

                    {!this.state.loading && (
                        <div className="mt-8 bg-white rounded-lg shadow-sm ring-1 ring-gray-200 overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900">Resultados</h3>
                                <p className="text-sm text-gray-600">
                                    {this.state.fines.length} multa(s) encontrada(s)
                                </p>
                            </div>
                            <div className="w-full">
                                <ResponsiveTable
                                    data={this.state.fines}
                                    columns={this.columns}
                                    onRowClick={(row) => this.openDetail(row)}
                                    renderActions={(row) => this.renderActions(row)}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <PenalitieDetailModal
                    open={this.state.detailOpen}
                    penalitie={this.state.selectedFine}
                    onClose={() => this.setState({ detailOpen: false })}
                />

                <ToastContainer position="top-center" hideProgressBar={true} />
            </div>
        );
    }

    render() {
        if (this.state.loadingPermissions) {
            return (
                <div className="text-center py-12">
                    <ClipLoader color="gray" size={50} />
                </div>
            );
        }
        return this.state.canSee ? this.renderBody() : <NoAccesComponent />;
    }
}

export default OfficialFines;
